import React from "react";
import { Button, Upload, Form } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import type { UploadFile, UploadProps } from "antd";
import { srmUploadService } from "@/utils/upload";
import { notifyError, notifySuccess } from "@/utils/notification";
import { FileListItem } from "@/components/FileListItem/FileListItem";
import { uploadSupplierFlowBusinessFiles } from "@/apis/flow.api";

// ==================== 类型定义 ====================

/**
 * 单个文件项数据结构
 */
export interface FileItem {
  name: string; // 文件名
  url: string; // 文件URL
  index: number; // 文件序号
}

/**
 * 文件上传字段配置
 */
export interface FileUploadFieldConfig {
  fieldName: string;
  initialFiles?: FileItem[];
  required?: boolean;
  readOnly?: boolean;
  fileType?: number; // 文件类型编码
}

// ==================== 常量配置 ====================

/**
 * 文件上传配置（基础配置，不包含customRequest）
 */
export const uploadProps: UploadProps = {
  name: "file",
  showUploadList: false,
  accept: ".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx",
  multiple: true, // 支持多文件选择
  maxCount: 10,
};

// ==================== 辅助函数 ====================

/**
 * 文件上传变化处理函数
 * 处理多文件上传的状态更新和通知
 */
export const onFileChange = (fieldName: string, form: any) => (info: any) => {
  // 仅更新表单值，移除通知逻辑（通知由批量上传API处理）
  if (info.fileList && info.fileList.length > 0) {
    form.setFieldsValue({
      [fieldName]: info.fileList.map((f: any) => ({
        ...f,
        url: f.response?.url || f.url,
        fileId: f.response?.fileId, // 保留从customRequest返回的fileId
      })),
    });
  }

  // 处理上传错误
  const errorFiles = info.fileList.filter((f: any) => f.status === "error");
  if (errorFiles.length > 0) {
    errorFiles.forEach((errorFile: any) => {
      notifyError(`${errorFile.name} 文件上传失败`);
    });
  }
};

/**
 * 从逗号隔开的文件URL字符串中解析单个文件对象数组
 * 支持文件名提取：如果URL是文件路径格式，则从中提取文件名
 *
 * @param fileUrlString 逗号隔开的文件URL字符串
 * @returns 文件对象数组
 */
export const parseFileUrls = (fileUrlString?: string): FileItem[] => {
  if (!fileUrlString || typeof fileUrlString !== "string") {
    return [];
  }

  return fileUrlString
    .split(",")
    .map((url, index) => {
      const trimmedUrl = url.trim();
      // 从URL中提取文件名（如果是完整路径，取最后一部分；如果是简单名称，直接使用）
      const fileName = trimmedUrl.includes("/")
        ? trimmedUrl.split("/").pop() || `文件${index + 1}`
        : trimmedUrl || `文件${index + 1}`;

      return {
        name: fileName,
        url: trimmedUrl,
        index,
      };
    })
    .filter((item) => item.url); // 过滤掉空URL
};

/**
 * 个别文件上传表单项组件
 * 使用 Ant Design Upload 组件处理文件上传
 */
export const ProfileFileFormField: React.FC<FileUploadFieldConfig> = ({
  fieldName,
  initialFiles = [],
  required = true,
  readOnly = false,
  fileType = 4, // 默认为"其他"
}) => {
  const form = Form.useFormInstance();
  // 用于收集待上传的文件，实现批量上传
  const uploadQueueRef = React.useRef<
    Array<{ url: string; fileName: string; onSuccess: Function }>
  >([]);
  const apiCallTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const totalFilesCountRef = React.useRef<number>(0);
  const completedFilesCountRef = React.useRef<number>(0);

  // TODO: normalizedInitialFiles 预留用于未来的初始化文件列表功能
  // 目前文件列表通过 Form.Item 的 fileList 直接管理
  // const normalizedInitialFiles = React.useMemo(() => {
  //   if (!initialFiles || initialFiles.length === 0) {
  //     return [];
  //   }
  //
  //   return initialFiles
  //     .map((file: any, index: number) => {
  //       // 如果已经是 FileItem 格式，直接返回
  //       if (file.name && file.url && file.index !== undefined) {
  //         return file;
  //       }
  //
  //       // 如果是 UploadFile 格式，转换为 FileItem
  //       if (file.url || file.response?.url) {
  //         const url = file.response?.url || file.url;
  //         const fileName =
  //           file.name || url.split("/").pop() || `文件${index + 1}`;
  //         return {
  //           name: fileName,
  //           url,
  //           index,
  //         };
  //       }
  //
  //       return null;
  //     })
  //     .filter(Boolean) as FileItem[];
  // }, [initialFiles]);

  /**
   * 批量调用API上传文件信息（所有文件在一个请求中）
   */
  const batchUploadFilesToAPI = React.useCallback(
    async (
      filesToUpload: Array<{
        url: string;
        fileName: string;
        onSuccess: Function;
      }>,
    ) => {
      if (filesToUpload.length === 0) return;

      try {
        // 准备批量上传的数据
        const fileDataList = filesToUpload.map((file) => ({
          fileUrl: file.url,
          fileName: file.fileName,
          fileType,
        }));

        console.log(
          `[批量上传] 调用一次API，上传${fileDataList.length}条文件数据:`,
          fileDataList,
        );

        // 一次API调用，传所有文件数据
        const response = await uploadSupplierFlowBusinessFiles(fileDataList);

        // 处理响应：返回的可能是数组或单个对象
        const fileIds = Array.isArray(response?.data?.data)
          ? response?.data?.data
          : response?.data?.data
            ? [response?.data?.data]
            : [];

        console.log(`[批量上传成功] 返回${fileIds.length}个fileId:`, fileIds);

        // 为每个文件调用对应的onSuccess，传递fileId
        filesToUpload.forEach((file, index) => {
          const fileId = fileIds[index];
          file.onSuccess?.({ url: file.url, fileId });
        });

        notifySuccess(`成功批量上传${filesToUpload.length}个文件`);
      } catch (apiError: any) {
        console.error("[批量上传失败]", apiError);
        // API调用失败时，仍然为所有文件调用onSuccess，因为文件已上传到OSS
        filesToUpload.forEach((file) => {
          file.onSuccess?.({ url: file.url });
        });
        notifyError("文件信息记录失败，但文件已成功上传到OSS");
      }
    },
    [fileType],
  );

  /**
   * 检查是否所有文件都已准备好，如果是则执行批量上传
   */
  const checkAndExecuteBatchUpload = React.useCallback(() => {
    completedFilesCountRef.current++;

    // 当所有文件都完成OSS上传时，执行一次API调用
    if (
      completedFilesCountRef.current >= totalFilesCountRef.current &&
      uploadQueueRef.current.length > 0
    ) {
      const filesToUpload = uploadQueueRef.current;
      uploadQueueRef.current = [];
      totalFilesCountRef.current = 0;
      completedFilesCountRef.current = 0;

      batchUploadFilesToAPI(filesToUpload);
    }
  }, [batchUploadFilesToAPI]);

  React.useEffect(() => {
    const timeoutId = apiCallTimeoutRef.current;
    return () => {
      if (timeoutId !== null) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  // ==================== 文件类型感知的上传处理 ====================
  const customRequest = async ({ file, onSuccess, onError }: any) => {
    try {
      // 获取文件名
      const fileName = (file as File).name;

      // 增加总文件计数
      totalFilesCountRef.current++;

      // 上传文件到OSS
      const url = await srmUploadService.uploadFile(file as File);

      console.log("单个文件OSS上传成功:", { fileName, url });

      // 将文件加入批量上传队列
      uploadQueueRef.current.push({
        url,
        fileName,
        onSuccess: (response: any) => {
          // 必须调用onSuccess，否则Upload组件状态不会更新
          onSuccess?.(response);
        },
      });

      // 检查是否可以执行批量API调用
      checkAndExecuteBatchUpload();
    } catch (err: any) {
      console.error("文件上传失败:", err);
      completedFilesCountRef.current++;
      checkAndExecuteBatchUpload();
      onError?.(err);
    }
  };

  return (
    <div>
      {!readOnly && (
        <div
          style={{
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <Form.Item
            name={fieldName}
            valuePropName="fileList"
            getValueFromEvent={(e) => {
              return e.fileList.map((file: UploadFile) => {
                return {
                  ...file,
                  url: file.response?.url || file.url,
                  fileId: file.response?.fileId,
                };
              });
            }}
            rules={
              required
                ? [
                    {
                      required: true,
                      message: `请上传${fieldName}`,
                    },
                  ]
                : []
            }
            style={{ margin: 0 }}
          >
            <Upload
              {...uploadProps}
              customRequest={customRequest}
              onChange={onFileChange(fieldName, form)}
            >
              <Button
                icon={<UploadOutlined />}
                variant="outlined"
                color="primary"
              >
                点击上传
              </Button>
            </Upload>
          </Form.Item>
        </div>
      )}

      {readOnly && (
        <div
          style={{
            marginBottom: "12px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        ></div>
      )}

      {/* 自定义文件列表：预览 + 下载 + 删除 */}
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue, setFieldsValue }) => {
          const fileList = form.getFieldValue(fieldName);
          if (!fileList) return null;

          const removeFile = (url?: string) => {
            if (readOnly) return; // 只读模式下不允许删除
            setFieldsValue({
              [fieldName]: fileList.filter((p: any) => p.url !== url),
            });
          };

          return (
            <div style={{ marginBottom: "12px" }}>
              {fileList.map((file: UploadFile, index: number) => (
                <FileListItem
                  key={`${fieldName}-${file.uid || file.url || ""}-${index}`}
                  file={file}
                  hiddenRemove={readOnly}
                  remove={removeFile}
                />
              ))}
            </div>
          );
        }}
      </Form.Item>
    </div>
  );
};
